import { LockIcon } from '../../icons';
import { cx } from '../../utils/cx';

const TOOLTIP_TEXT = 'Internal only';

interface IInternalOnlyBadgeProps {
  /** Position / sizing overrides. Merged with the default `absolute -right-1 -top-1 size-3.5`. */
  className?: string;
}

/**
 * Small warning-tone padlock pip designed to overlay an icon (e.g. a
 * sidebar nav icon) and signal that the surrounding feature is gated to
 * internal users only. Native `title="Internal only"` provides a hover
 * tooltip for sighted users; `aria-hidden` keeps the badge out of the
 * assistive-tech tree (consumers expose the gated state via the
 * surrounding element's accessible name when needed).
 *
 * Render the badge as a sibling of the icon inside a `relative`-positioned
 * wrapper:
 *
 *     <span className="relative inline-flex">
 *       <SomeIcon className={...} />
 *       <InternalOnlyBadge />
 *     </span>
 *
 * For label-adjacent placement (e.g. inside a dropdown item's text) where
 * there is no icon to overlay, use {@link InlineInternalOnlyBadge} instead.
 *
 * `title` over the proper `Tooltip` primitive deliberately: this badge
 * lives inside interactive ancestors (sidebar links, dropdown items), and
 * `Tooltip`'s `TooltipTrigger` wraps its child in `AriaButton`, which
 * would nest a button inside another button/link — invalid HTML. The
 * broader migration off `title` is tracked in ORN-657.
 */
const InternalOnlyBadge = ({ className }: IInternalOnlyBadgeProps) => (
  <span
    aria-hidden="true"
    title={TOOLTIP_TEXT}
    className={cx(
      'bg-warning-solid absolute -right-1 -top-1 flex size-3.5 items-center justify-center rounded-full text-white',
      className
    )}
  >
    <LockIcon className="size-2.5" strokeWidth={2.5} />
  </span>
);

/**
 * Inline sibling of {@link InternalOnlyBadge} for use next to a label
 * string (e.g. inside a `Dropdown.Item`'s children, after a sidebar
 * label) where there's no icon to overlay. Same warning-tone padlock
 * pip as the default export, vertically centered against the
 * surrounding text via `align-middle` so it reads as a peer of the
 * label. Native `title="Internal only"` exposes the meaning on hover;
 * `aria-hidden` keeps the badge out of the assistive-tech tree.
 *
 *     <Dropdown.Item onAction={…}>
 *       Sentry test
 *       <InlineInternalOnlyBadge />
 *     </Dropdown.Item>
 */
export const InlineInternalOnlyBadge = ({ className }: IInternalOnlyBadgeProps) => (
  <span
    aria-hidden="true"
    title={TOOLTIP_TEXT}
    className={cx(
      'bg-warning-solid ml-1 inline-flex size-3.5 shrink-0 items-center justify-center rounded-full align-middle text-white',
      className
    )}
  >
    <LockIcon className="size-2.5" strokeWidth={2.5} />
  </span>
);

export default InternalOnlyBadge;
